import type { MeetingType, UserRole } from "@scoach/types";
import { isCERole } from "@scoach/types";
import { Image, Spark, Users } from "@scoach/ui/icons";
import { useCallback, useMemo, useRef, useState } from "react";

import { ErrorBoundary } from "../../../../components/ErrorBoundary.tsx";
import { useAuthStore } from "../../../auth/store.ts";
import { useLiveMeetingStore } from "../store.ts";
import { CaptionsPanel } from "./CaptionsPanel.tsx";
import { InfographicImagePanel } from "./InfographicImagePanel.tsx";
import { DIAGRAM_MIN_CONTEXT_LINES, LiveDiagramPanel } from "./LiveDiagramPanel.tsx";
import { QuietBar } from "./QuietBar.tsx";
import { TranscriptView } from "./TranscriptView.tsx";
import { UseCasesPanel } from "./UseCasesPanel.tsx";
import { WhatsNewPanel } from "./WhatsNewPanel.tsx";

export interface TranscriptPanelProps {
  meetingId: string;
  meetingType?: MeetingType;
  /** The role chosen for THIS meeting (overrides the user's profile role for gating). */
  meetingRole?: UserRole;
  /** True once the meeting doc has loaded, so the default-tab decision uses the real
   * meeting role rather than firing early on the profile role. */
  roleKnown?: boolean;
}

export function TranscriptPanel({ meetingId, meetingType, meetingRole }: TranscriptPanelProps) {
  // Quick Ask height resizer state, stored in localStorage
  const [qaHeight, setQaHeight] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("sellsynq_qa_height");
      if (saved) {
        const parsed = Number.parseInt(saved, 10);
        if (!Number.isNaN(parsed) && parsed >= 65 && parsed <= 900) return parsed;
      }
    } catch {}
    return 240;
  });

  const panelRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHRef = useRef(0);

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      startYRef.current = e.clientY;
      startHRef.current = qaHeight;

      const onMouseMove = (ev: MouseEvent) => {
        if (!isDraggingRef.current) return;
        const deltaY = startYRef.current - ev.clientY;
        const panelH = panelRef.current?.clientHeight || 700;
        const maxH = Math.max(200, Math.floor(panelH * 0.75));
        const nextH = Math.max(72, Math.min(maxH, startHRef.current + deltaY));
        setQaHeight(nextH);
      };

      const onMouseUp = () => {
        isDraggingRef.current = false;
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        try {
          setQaHeight((curr) => {
            localStorage.setItem("sellsynq_qa_height", String(curr));
            return curr;
          });
        } catch {}
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [qaHeight],
  );

  const setPreset = useCallback((preset: "compact" | "standard" | "expanded") => {
    let nextH = 240;
    if (preset === "compact") nextH = 74;
    else if (preset === "expanded") {
      const panelH = panelRef.current?.clientHeight || 700;
      nextH = Math.max(380, Math.floor(panelH * 0.6));
    }
    setQaHeight(nextH);
    try {
      localStorage.setItem("sellsynq_qa_height", String(nextH));
    } catch {}
  }, []);
  const muted = useLiveMeetingStore((s) => s.muted);
  const listening = useLiveMeetingStore((s) => s.listening);
  const rightTab = useLiveMeetingStore((s) => s.rightPanelTab);
  const setRightTab = useLiveMeetingStore((s) => s.setRightPanelTab);
  const diagrams = useLiveMeetingStore((s) => s.diagrams);
  const unseenDiagrams = useLiveMeetingStore((s) => s.unseenDiagrams);
  const profileRole = useAuthStore((s) => s.user?.role);
  // The meeting's chosen role wins over the user's profile role (mirrors the backend's
  // effectiveRole). This is what decides CE-specific UI like the Diagram tab.
  const effectiveRole = meetingRole ?? profileRole;
  const hints = useLiveMeetingStore((s) => s.hints);
  const activeTag = useLiveMeetingStore((s) => s.activeTagFilter);
  const setActiveTag = useLiveMeetingStore((s) => s.setActiveTagFilter);
  // Aggregate the unique keyword tags across current hints for the filter bar
  // shown above Quiet Ask. Clicking a tag filters the hints in the coach column.
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const h of hints) for (const t of h.tags ?? []) set.add(t);
    return [...set].sort((a, b) => a.localeCompare(b)).slice(0, 12);
  }, [hints]);
  // The Diagram tab is available to Customer Engineers and on technical meetings
  // (they can generate one on demand), and also whenever a diagram already exists.
  const isCE = isCERole(effectiveRole);
  const showDiagramTab = isCE || meetingType === "technical" || diagrams.length > 0;

  // "Ready to generate" indicator: enough conversation captured + no diagram of that kind
  // yet, so the user knows they can draw one now.
  const transcriptLen = useLiveMeetingStore((s) => s.transcript.length);
  const canGenDiagram = transcriptLen >= DIAGRAM_MIN_CONTEXT_LINES;
  const archCount = diagrams.filter((d) => d.kind !== "flowchart").length;
  const flowCount = diagrams.filter((d) => d.kind === "flowchart").length;
  const readyDot = (
    <span
      title="Ready to generate"
      aria-label="Ready to generate"
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        marginLeft: 6,
        borderRadius: "50%",
        background: "var(--gc-green, #1e8e3e)",
      }}
    />
  );

  // Everyone lands on the Infographic tab (the first tab, the store default) —
  // the Diagram/Decision tabs are one click away for CEs.

  const diagramButton = showDiagramTab ? (
    <button
      type="button"
      className={rightTab === "diagram" ? "on" : ""}
      onClick={() => setRightTab("diagram")}
    >
      <Image size={13} /> Diagram
      {unseenDiagrams && rightTab !== "diagram" && (
        <span
          title="New diagram"
          aria-label="New diagram"
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            marginLeft: 6,
            borderRadius: "50%",
            background: "var(--gc-red, #d93025)",
          }}
        />
      )}
      {canGenDiagram && archCount === 0 && rightTab !== "diagram" && readyDot}
    </button>
  ) : null;

  const decisionButton = showDiagramTab ? (
    <button
      type="button"
      className={rightTab === "decisiontree" ? "on" : ""}
      onClick={() => setRightTab("decisiontree")}
    >
      <Image size={13} /> Decision tree
      {canGenDiagram && flowCount === 0 && rightTab !== "decisiontree" && readyDot}
    </button>
  ) : null;

  return (
    <section ref={panelRef} className="panel transcript-panel" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div className="panel-head">
        <div className="panel-title-row">
          <div className="seg seg-sm right-tab-seg">
            <button
              type="button"
              className={rightTab === "infographic" ? "on" : ""}
              onClick={() => setRightTab("infographic")}
            >
              <Image size={13} /> Infographic
            </button>
            {isCE && diagramButton}
            {isCE && decisionButton}
            <button
              type="button"
              className={rightTab === "whatsnew" ? "on" : ""}
              onClick={() => setRightTab("whatsnew")}
            >
              <Spark size={13} /> What's New
            </button>
            <button
              type="button"
              className={rightTab === "usecase" ? "on" : ""}
              onClick={() => setRightTab("usecase")}
            >
              <Users size={13} /> Use Cases
            </button>
            {!isCE && diagramButton}
            {!isCE && decisionButton}
            <button
              type="button"
              className={rightTab === "transcript" ? "on" : ""}
              onClick={() => setRightTab("transcript")}
            >
              Transcript
              {listening && !muted && (
                <span
                  className="rec-dot"
                  title="Recording"
                  aria-label="Recording"
                  style={{ marginLeft: 6 }}
                />
              )}
            </button>
            <button
              type="button"
              className={rightTab === "captions" ? "on" : ""}
              onClick={() => setRightTab("captions")}
            >
              Captions
            </button>
          </div>
        </div>
      </div>

      <div className="transcript-panel-body" style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        <ErrorBoundary label={`live-panel:${rightTab}`}>
          {rightTab === "infographic" ? (
            <InfographicImagePanel meetingId={meetingId} />
          ) : rightTab === "whatsnew" ? (
            <WhatsNewPanel />
          ) : rightTab === "usecase" ? (
            <UseCasesPanel />
          ) : rightTab === "diagram" ? (
            <LiveDiagramPanel kind="architecture" />
          ) : rightTab === "decisiontree" ? (
            <LiveDiagramPanel kind="flowchart" />
          ) : rightTab === "captions" ? (
            <CaptionsPanel />
          ) : (
            <TranscriptView />
          )}
        </ErrorBoundary>

        {allTags.length > 0 && (
          <div className="hint-tag-filterbar">
            <span className="hint-tag-filterbar-label">Filter hints:</span>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`tag-chip${activeTag === tag ? " on" : ""}`}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                title={`Show only hints tagged "${tag}"`}
              >
                {tag}
              </button>
            ))}
            {activeTag && (
              <button type="button" className="ghost-btn xs" onClick={() => setActiveTag(null)}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Resizable splitter between top tab panel and Quick Ask */}
      <div
        className="qa-resize-splitter"
        onMouseDown={startDrag}
        onDoubleClick={() => setPreset(qaHeight <= 100 ? "standard" : "compact")}
        title="Drag up or down to resize Quick Ask (double-click to toggle)"
      >
        <div className="qa-splitter-grip">
          <span className="qa-grip-bar" />
          <span className="qa-splitter-label">Quick Ask</span>
        </div>
        <div className="qa-splitter-presets" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={`qa-preset-btn ${qaHeight <= 90 ? "active" : ""}`}
            onClick={() => setPreset("compact")}
            title="Compact Quick Ask (maximize top view)"
          >
            _ Compact
          </button>
          <button
            type="button"
            className={`qa-preset-btn ${qaHeight > 90 && qaHeight < 340 ? "active" : ""}`}
            onClick={() => setPreset("standard")}
            title="Default size"
          >
            ■ Default
          </button>
          <button
            type="button"
            className={`qa-preset-btn ${qaHeight >= 340 ? "active" : ""}`}
            onClick={() => setPreset("expanded")}
            title="Expanded Quick Ask (large view)"
          >
            ⤢ Expand
          </button>
        </div>
      </div>

      <QuietBar meetingId={meetingId} height={qaHeight} onResizePreset={setPreset} />
    </section>
  );
}
